import { useEffect, useRef } from 'react';
import type { InputKey } from '../engine/types';

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/** A control the player deliberately focused with the keyboard (Tab), not by tapping it. */
function isKeyboardFocusedControl(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (!el.matches('button, a[href], [role="button"], [role="switch"], [role="tab"]')) return false;
  try {
    return el.matches(':focus-visible');
  } catch {
    return false;
  }
}

/** The letter a key press means: the printed key first, then the physical key on non-Latin layouts. */
export function letterFromEvent(e: Pick<KeyboardEvent, 'key' | 'code'>): string | null {
  if (/^[a-zA-Z]$/.test(e.key)) return e.key.toLowerCase();
  if (e.key.length === 1) {
    const m = /^Key([A-Z])$/.exec(e.code || '');
    if (m) return m[1].toLowerCase();
  }
  return null;
}

/**
 * Bridge the physical keyboard into the unified InputKey pipeline. Letters,
 * Backspace, and Enter become InputKeys; modifier combos, IME composition and
 * keystrokes aimed at text fields are left alone.
 *
 * Enter is claimed (preventDefault) so it doesn't *also* click whatever button a
 * mouse/touch left focused (e.g. taking a second hint) — unless the player Tabbed
 * to that control, in which case Enter keeps its normal meaning.
 */
export function usePhysicalKeyboard(onKey: (key: InputKey) => void, active = true): void {
  const onKeyRef = useRef(onKey);
  useEffect(() => {
    onKeyRef.current = onKey;
  });

  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      if (isTypingTarget(e.target)) return;

      if (e.key === 'Enter') {
        if (e.repeat || isKeyboardFocusedControl(e.target)) return;
        e.preventDefault();
        onKeyRef.current({ type: 'enter' });
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        onKeyRef.current({ type: 'backspace' });
      } else {
        const letter = letterFromEvent(e);
        if (letter) onKeyRef.current({ type: 'char', value: letter });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active]);
}
